import {
  signInWithEmailAndPassword,
  signOut,
  User,
  sendPasswordResetEmail
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { databaseService, Company } from './database/databaseService';

import { CompanyData, AuthUser } from '../types/auth';

/** Non-sensitive pending-registration context shown on /verify-email/:id. */
export interface PendingRegistrationInfo {
  found: boolean;
  email?: string;
  companyName?: string;
  firstName?: string;
  language?: string;
}

/**
 * Structured outcome of completeRegistration (mirrors the Cloud Function
 * result payload — code outcomes are data, not exceptions).
 */
export type CompleteRegistrationResult =
  | { status: 'success'; companyId?: string }
  | { status: 'wrong'; attemptsRemaining: number }
  | { status: 'expired' }
  | { status: 'locked' }
  | { status: 'not-found' };

// Server-side HttpsError messages are i18n keys (e.g. auth.errors.email_already_in_use).
const I18N_KEY_RE = /^[a-z0-9_]+(\.[a-z0-9_]+)+$/i;

class AuthService {
  // Registrácia novej firmy — verify-before-create: uloží pending registráciu
  // server-side a pošle 6-ciferný kód e-mailom. ŽIADEN Auth účet ani Firestore
  // doc tu nevzniká (a heslo sa tu vôbec nezbiera) — účet vytvorí až
  // completeRegistration po zadaní správneho kódu na /verify-email/:id.
  async registerCompany(email: string, companyData: CompanyData, language: string): Promise<{ id: string }> {
    try {
      const createPending = httpsCallable(functions, 'createPendingRegistration');
      const result = await createPending({
        email,
        language,
        name: companyData.name,
        street: companyData.street,
        streetNumber: companyData.streetNumber,
        city: companyData.city,
        postalCode: companyData.postalCode,
        state: companyData.state,
        country: companyData.country || '',
        ico: companyData.ico || '',
        dic: companyData.dic || '',
        icDph: companyData.icDph || '',
        companyType: companyData.companyType,
        contactPerson: {
          firstName: companyData.contactPerson.firstName,
          lastName: companyData.contactPerson.lastName,
          position: companyData.contactPerson.position,
          phone: companyData.contactPerson.phone,
        },
      });

      const id = (result.data as { id?: string } | null)?.id;
      if (!id) {
        throw new Error('auth.errors.unknown_error');
      }
      return { id };
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error(this.getCallableErrorKey(error));
    }
  }

  // Kontext pending registrácie pre verify stránku (found:false = neexistuje/expirovaná).
  async getPendingRegistration(id: string): Promise<PendingRegistrationInfo> {
    try {
      const getPending = httpsCallable(functions, 'getPendingRegistration');
      const result = await getPending({ id });
      const data = result.data as PendingRegistrationInfo | null;
      if (!data || typeof data.found !== 'boolean') {
        return { found: false };
      }
      return data;
    } catch (error) {
      console.error('getPendingRegistration error:', error);
      throw new Error(this.getCallableErrorKey(error));
    }
  }

  // Dokončenie registrácie: overí kód a server-side vytvorí Auth účet
  // (emailVerified: true) + companies/users docy. Výsledky kódu (wrong/expired/
  // locked/not-found) prichádzajú ako dáta; ostatné chyby ako i18n Error.
  async completeRegistration(id: string, code: string, password: string): Promise<CompleteRegistrationResult> {
    try {
      const complete = httpsCallable(functions, 'completeRegistration');
      const result = await complete({ id, code, password });
      const data = result.data as CompleteRegistrationResult | null;
      if (!data || typeof data.status !== 'string') {
        throw new Error('auth.errors.unknown_error');
      }
      return data;
    } catch (error) {
      console.error('completeRegistration error:', error);
      throw new Error(this.getCallableErrorKey(error));
    }
  }

  // Maps a callable failure to an i18n key: prefer the server-provided key in
  // the message, otherwise map the functions/* error code.
  private getCallableErrorKey(error: unknown): string {
    const message = error instanceof Error ? error.message : '';
    if (I18N_KEY_RE.test(message)) {
      return message;
    }
    if (error instanceof FirebaseError) {
      switch (error.code.replace('functions/', '')) {
        case 'already-exists':
          return 'auth.errors.email_already_in_use';
        case 'resource-exhausted':
          return 'auth.errors.too_many_requests';
        case 'unavailable':
          return 'registration.errors.email_send_failed';
        case 'failed-precondition':
          return 'auth.errors.app_check_failed';
        case 'invalid-argument':
          return 'registration.errors.missing_fields';
        default:
          return 'auth.errors.unknown_error';
      }
    }
    return 'auth.errors.unknown_error';
  }

  // Prihlásenie
  async login(email: string, password: string): Promise<AuthUser> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Načítanie údajov firmy
      const companyData = await this.getCompanyData(user.uid);

      // Aktualizácia lastLoginAt
      if (companyData) {
        await databaseService.updateCompany(companyData.id, {
          lastLoginAt: new Date() as unknown as import('firebase/firestore').Timestamp,
        });
      }

      return {
        uid: user.uid,
        email: user.email || '',
        emailVerified: user.emailVerified,
        companyData
      };
    } catch (error) {
      const code = error instanceof FirebaseError ? error.code : 'auth/unknown-error';
      throw new Error(this.getErrorMessage(code));
    }
  }

  // Odhlásenie
  async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch {
      throw new Error('auth.errors.unknown_error');
    }
  }

  // Získanie údajov firmy
  async getCompanyData(uid: string): Promise<Company | undefined> {
    try {
      // Skontroluj či máme platné UID
      if (!uid) {
        return undefined;
      }

      // Najprv nájdeme používateľa podľa UID
      const user = await databaseService.getUser(uid);

      if (!user) {
        // Skúsime nájsť firmu podľa emailu používateľa
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.email) {
          const companyByEmail = await databaseService.getCompanyByEmail(currentUser.email);
          if (companyByEmail) {
            return companyByEmail;
          }
        }

        return undefined;
      }

      // Skontroluj či má používateľ companyId
      if (!user.companyId) {

        // Skúsime nájsť firmu podľa emailu ako fallback
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.email) {
          const companyByEmail = await databaseService.getCompanyByEmail(currentUser.email);
          if (companyByEmail) {
            return companyByEmail;
          }
        }

        return undefined;
      }

      // Potom načítame firmu
      const company = await databaseService.getCompany(user.companyId);

      if (!company) {
        // Skúsime nájsť firmu podľa emailu ako fallback
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.email) {
          const companyByEmail = await databaseService.getCompanyByEmail(currentUser.email);
          if (companyByEmail) {
            return companyByEmail;
          }
        }
      }

      return company || undefined;
    } catch (error) {
      console.error('Chyba pri načítaní údajov firmy:', error);
      return undefined;
    }
  }

  // Kontrola či je email overený
  async checkEmailVerification(): Promise<boolean> {
    const user = auth.currentUser;
    if (user) {
      await user.reload();
      return user.emailVerified;
    }
    return false;
  }

  // Odoslanie reset hesla
  async sendPasswordReset(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      const code = error instanceof FirebaseError ? error.code : 'auth/unknown-error';
      throw new Error(this.getErrorMessage(code));
    }
  }

  // Aktuálny používateľ
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  // Returns i18n translation keys for auth errors
  private getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'auth.errors.email_already_in_use';
      case 'auth/invalid-email':
        return 'auth.errors.invalid_email';
      case 'auth/weak-password':
        return 'auth.errors.weak_password';
      case 'auth/user-not-found':
        return 'auth.errors.user_not_found';
      case 'auth/wrong-password':
        return 'auth.errors.wrong_password';
      case 'auth/too-many-requests':
        return 'auth.errors.too_many_requests';
      case 'auth/network-request-failed':
        return 'auth.errors.network_error';
      default:
        return 'auth.errors.unknown_error';
    }
  }

  async resendVerificationEmail(email: string): Promise<void> {
    try {
      const resendEmail = httpsCallable(functions, 'resendVerificationEmail');
      await resendEmail({ email });
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      throw new Error('auth.errors.resend_verification_failed');
    }
  }
}

export const authService = new AuthService();
export default authService; 